import { FC, useState } from 'react';
import { Box, Divider, Paper, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { TooltipCard } from '@components';
import { useContentDirection, useDirection } from '@hooks';
import { useTrans } from '@libs/i18n';
import {
  CaretDownIcon,
  CaretUpIcon,
  ClockIcon,
  LightningIcon,
  WarningDiamondIcon,
} from '@phosphor-icons/react';
import { NoFlipBox } from '@utils';

import { DomainResultViewModel } from '../../types';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatTime(minutes: number): string {
  if (!minutes || minutes <= 0) return '';
  if (minutes < 60) return `${minutes}m`;
  const h = minutes / 60;
  return h % 1 === 0 ? `${h}h` : `${h.toFixed(1)}h`;
}

/** Map maturity percentage → urgency i18n key + color */
function urgency(pct: number): { labelKey: string; color: string } {
  if (pct < 20) return { labelKey: 'assessments.overview.priorityActions.urgency.critical', color: '#DC2626' };
  if (pct < 40) return { labelKey: 'assessments.overview.priorityActions.urgency.high',     color: '#EA580C' };
  if (pct < 60) return { labelKey: 'assessments.overview.priorityActions.urgency.medium',   color: '#D97706' };
  return           { labelKey: 'assessments.overview.priorityActions.urgency.low',      color: '#16A34A' };
}

// ── Action step ───────────────────────────────────────────────────────────────

interface ActionItemProps {
  orderIndex: number;
  title: string;
  description: string;
}

const ActionItem: FC<ActionItemProps> = observer(({ orderIndex, title, description }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
      <Box
        sx={{
          width: 20, height: 20,
          borderRadius: '50%',
          backgroundColor: theme.palette.grey[100],
          border: `1px solid ${theme.palette.grey[300]}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, mt: 0.2,
        }}
      >
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.secondary', lineHeight: 1 }}>
          {orderIndex}
        </Typography>
      </Box>
      <Box>
        <Typography variant="textSM_semiBold" sx={{ color: 'text.primary', lineHeight: 1.4, display: 'block' }}>
          {title}
        </Typography>
        <Typography variant="textXS_regular" sx={{ color: 'text.tertiary', mt: 0.5, lineHeight: 1.55, display: 'block' }}>
          {description}
        </Typography>
      </Box>
    </Box>
  );
});

// ── Domain card ───────────────────────────────────────────────────────────────

interface DomainActionCardProps {
  domain: DomainResultViewModel;
  rank: number;
}

const DomainActionCard: FC<DomainActionCardProps> = observer(({ domain, rank }) => {
  const theme = useTheme();
  const { t } = useTrans();
  const urg = urgency(domain.maturityScore);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const urgLabel = t(urg.labelKey as any);
  const topActions = domain.whatIsNeeded.slice(0, 2);

  // Effort: show P50 as primary, full range in tooltip
  const minT = domain.minEstimatedTime;
  const maxT = domain.maxEstimatedTime;
  const p50 = minT && maxT ? Math.round((minT + maxT) / 2) : (minT || maxT);
  const p50Label = p50 ? `~${formatTime(p50)}` : null;
  const rangeLabel = (minT && maxT && minT !== maxT)
    ? `${formatTime(minT)}–${formatTime(maxT)}`
    : null;

  return (
    <Box
      sx={{
        flex: '1 1 280px', minWidth: 0,
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 2, overflow: 'hidden',
        backgroundColor: 'white',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Urgency accent bar */}
      <Box sx={{ height: 3, backgroundColor: urg.color, flexShrink: 0 }} />

      {/* Header */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
        {/* Rank + domain name */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Box
              sx={{
                width: 22, height: 22, borderRadius: 1,
                backgroundColor: urg.color, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'white' }}>#{rank}</Typography>
            </Box>
            <Typography
              variant="textSM_semiBold"
              sx={{ color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {domain.domainName}
            </Typography>
          </Box>
          <LightningIcon size={16} color={urg.color} weight="fill" style={{ flexShrink: 0, marginTop: 2 }} />
        </Box>

        {/* Badges row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Box
            sx={{
              px: 1, py: 0.25, borderRadius: 1,
              backgroundColor: `${urg.color}14`, border: `1px solid ${urg.color}40`,
              display: 'inline-flex', alignItems: 'center', gap: 0.5,
            }}
          >
            <WarningDiamondIcon size={11} color={urg.color} weight="fill" />
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: urg.color }}>{urgLabel}</Typography>
          </Box>

          <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }}>
            {Math.round(domain.maturityScore)}% maturity
          </Typography>

          {p50Label && (
            <TooltipCard
              variant="dark"
              placement="top"
              description={rangeLabel
                ? t('assessments.overview.priorityActions.effortRange', { range: rangeLabel })
                : ''}
            >
              <Box
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.4,
                  cursor: rangeLabel ? 'help' : 'default',
                }}
              >
                <ClockIcon size={11} color={theme.palette.text.disabled as string} />
                <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }}>
                  {t('assessments.overview.priorityActions.effort', { time: p50Label })}{rangeLabel ? ' ⓘ' : ''}
                </Typography>
              </Box>
            </TooltipCard>
          )}
        </Box>

        {/* Risk callout */}
        {domain.theRisk && (
          <Box
            sx={{
              mt: 1.5, px: 1.5, py: 1, borderRadius: 1,
              backgroundColor: `${urg.color}08`,
              borderInlineStart: `3px solid ${urg.color}`,
            }}
          >
            <Typography variant="textXS_regular" sx={{ color: 'text.secondary', lineHeight: 1.55 }}>
              {domain.theRisk.length > 200
                ? `${domain.theRisk.slice(0, 197)}…`
                : domain.theRisk}
            </Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ borderColor: 'grey.100', mx: 2.5 }} />

      {/* Recommended actions */}
      <Box sx={{ px: 2.5, pt: 1.5, pb: 2.5, flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography
          variant="textXS_semiBold"
          sx={{
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontSize: 10,
          }}
        >
          {t('assessments.overview.priorityActions.recommendedActions')}
        </Typography>

        {topActions.length > 0
          ? topActions.map((action) => (
              <ActionItem
                key={action.orderIndex}
                orderIndex={action.orderIndex}
                title={action.title}
                description={action.description}
              />
            ))
          : (
            <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }}>
              {t('assessments.overview.priorityActions.noActions')}
            </Typography>
          )}
      </Box>
    </Box>
  );
});

// ── PriorityActionsPanel ──────────────────────────────────────────────────────

interface PriorityActionsPanelProps {
  domainResults: DomainResultViewModel[];
}

export const PriorityActionsPanel: FC<PriorityActionsPanelProps> = observer(
  ({ domainResults }) => {
  const theme = useTheme();
  const { t } = useTrans();
  // The panel's own chrome is interface text, so it follows the UI. The domain
  // cards are AI prose frozen in the assessment's language, so they follow the
  // content language (see ContentLanguageProvider).
  const { dir: uiDir } = useDirection();
  const { dir: contentDir, lang: contentLang } = useContentDirection();
  const [collapsed, setCollapsed] = useState(false);

  // Bottom 3 applicable domains by maturity score, lowest first → #1 is always leftmost
  const weakest = [...domainResults]
    .filter((d) => d.isApplicable && !d.isSkipped)
    .sort((a, b) => (a.maturityScore ?? 0) - (b.maturityScore ?? 0))
    .slice(0, 3);

  if (weakest.length === 0) return null;
  if (weakest.every((d) => d.whatIsNeeded.length === 0)) return null;

  return (
    <Paper
      variant="panel"
      elevation={0}
      sx={{ border: `1px solid ${theme.palette.grey[300]}`, backgroundColor: 'white' }}
    >
      {/* Panel header — interface text, so it follows the UI direction. */}
      <NoFlipBox
        mode={uiDir}
        sx={{
          display: 'flex', alignItems: 'flex-start',
          justifyContent: 'space-between',
          mb: collapsed ? 0 : 2.5,
          flexWrap: 'wrap', gap: 1,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <LightningIcon size={16} color={theme.palette.warning.main} weight="fill" />
            <Typography variant="textLG_medium" sx={{ color: 'text.primary' }}>
              {t('assessments.overview.priorityActions.title')}
            </Typography>
          </Box>
          {!collapsed && (
            <Typography variant="textSM_regular" sx={{ color: 'text.tertiary' }}>
              {t('assessments.overview.priorityActions.subtitle', { count: weakest.length })}
            </Typography>
          )}
        </Box>

        {/* Collapse toggle */}
        <Box
          component="button"
          onClick={() => setCollapsed((v) => !v)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.5,
            px: 1.5, py: 0.75, borderRadius: 1.5,
            border: `1px solid ${theme.palette.grey[200]}`,
            backgroundColor: 'transparent',
            cursor: 'pointer', font: 'inherit',
            color: theme.palette.text.secondary,
            '&:hover': { backgroundColor: theme.palette.grey[50] },
          }}
        >
          <Typography variant="textXS_regular" sx={{ color: 'text.secondary' }}>
            {collapsed
              ? t('assessments.overview.priorityActions.show')
              : t('assessments.overview.priorityActions.hide')}
          </Typography>
          {collapsed
            ? <CaretDownIcon size={13} />
            : <CaretUpIcon size={13} />}
        </Box>
      </NoFlipBox>

      {/* Domain cards — AI-written remediation steps. Pinned to the language
          they were generated in, so the prose aligns correctly and rank #1
          leads that language's reading direction. */}
      {!collapsed && (
        <NoFlipBox
          mode={contentDir}
          lang={contentLang ?? undefined}
          sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'stretch' }}
        >
          {weakest.map((domain, idx) => (
            <DomainActionCard key={domain.domainId} domain={domain} rank={idx + 1} />
          ))}
        </NoFlipBox>
      )}
    </Paper>
  );
});
